import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Camera, FileText, Check, Truck, MapPin, Loader2, AlertCircle, Lock, Navigation } from 'lucide-react';
import SignaturePad from '../components/SignaturePad';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DriverDelivery = () => {
  // Auth state
  const [jobId, setJobId] = useState('');
  const [pin, setPin] = useState('');
  const [delivery, setDelivery] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [authError, setAuthError] = useState('');

  // POD state
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signatureBlob, setSignatureBlob] = useState(null);
  const [podNotes, setPodNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const fileInputRef = useRef(null);

  // GPS state
  const [gpsStatus, setGpsStatus] = useState('idle'); // idle, active, error
  const [lastGpsTime, setLastGpsTime] = useState(null);
  const gpsIntervalRef = useRef(null);

  const sendLocation = useCallback(async () => {
    if (!delivery || !pin) return;
    if (!navigator.geolocation) { setGpsStatus('error'); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const formData = new FormData();
          formData.append('lat', pos.coords.latitude);
          formData.append('lng', pos.coords.longitude);
          formData.append('pin', pin.trim());
          await axios.post(`${API}/deliver/${delivery.id}/location`, formData);
          setGpsStatus('active');
          setLastGpsTime(new Date());
        } catch {
          setGpsStatus('error');
        }
      },
      () => setGpsStatus('error'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [delivery, pin]);

  // Auto-send GPS every 30 seconds
  useEffect(() => {
    if (!delivery || submitted) return;
    sendLocation(); // Send immediately
    gpsIntervalRef.current = setInterval(sendLocation, 30000);
    return () => { if (gpsIntervalRef.current) clearInterval(gpsIntervalRef.current); };
  }, [delivery, submitted, sendLocation]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!jobId.trim() || !pin.trim()) return;
    setAuthError('');
    setVerifying(true);
    try {
      const formData = new FormData();
      formData.append('job_id', jobId.trim().toUpperCase());
      formData.append('pin', pin.trim());
      const res = await axios.post(`${API}/deliver/verify`, formData);
      setDelivery(res.data);
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Invalid Job ID or PIN');
    } finally {
      setVerifying(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setSubmitError('Photo too large (max 10MB)');
      return;
    }
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!photo && !signatureBlob) {
      setSubmitError('Please provide at least a photo or signature');
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('pin', pin.trim());
      formData.append('pod_notes', podNotes);
      if (photo) formData.append('photo', photo);
      if (signatureBlob) formData.append('signature', new File([signatureBlob], 'signature.png', { type: 'image/png' }));
      await axios.post(`${API}/deliver/${delivery.id}/submit`, formData);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-brand-navy flex items-center justify-center px-4" data-testid="pod-success">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="font-heading text-3xl text-white mb-3">DELIVERY CONFIRMED</h1>
          <p className="text-slate-400 mb-2">Proof of delivery for <span className="text-brand-red font-heading">{delivery?.id}</span> has been submitted.</p>
          <p className="text-slate-500 text-sm">The customer will be notified automatically.</p>
        </motion.div>
      </div>
    );
  }

  // Verify screen
  if (!delivery) {
    return (
      <div className="min-h-screen bg-brand-navy flex items-center justify-center px-4" data-testid="driver-verify-page">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="font-heading text-2xl mb-1">
              <span className="text-brand-red">TOP</span><span className="text-white">FLEET</span>
            </div>
            <p className="text-slate-400 text-sm">Driver Delivery Portal</p>
          </div>
          <form onSubmit={handleVerify} className="bg-brand-surface rounded-2xl border border-white/10 p-6 space-y-4">
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">Job ID</label>
              <input
                type="text"
                value={jobId}
                onChange={(e) => setJobId(e.target.value)}
                placeholder="TF-XXXXX"
                className="mt-1 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:border-brand-red outline-none text-center font-heading text-lg tracking-wider"
                data-testid="driver-job-id-input"
                required
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">Driver PIN</label>
              <input
                type="text"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="4-digit PIN"
                maxLength={4}
                inputMode="numeric"
                className="mt-1 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:border-brand-red outline-none text-center font-heading text-2xl tracking-[0.5em]"
                data-testid="driver-pin-input"
                required
              />
            </div>
            {authError && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg" data-testid="driver-auth-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {authError}
              </div>
            )}
            <button
              type="submit"
              disabled={verifying}
              className="w-full py-3 bg-brand-red hover:bg-red-700 text-white font-heading rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="driver-verify-button"
            >
              {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
              {verifying ? 'VERIFYING...' : 'ACCESS DELIVERY'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // POD submission screen
  return (
    <div className="min-h-screen bg-brand-navy" data-testid="driver-pod-page">
      <header className="bg-brand-surface border-b border-white/10 py-4 px-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="font-heading text-lg">
            <span className="text-brand-red">TOP</span><span className="text-white">FLEET</span>
          </div>
          <span className="text-brand-red font-heading">{delivery.id}</span>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Delivery Info */}
        <div className="bg-brand-surface rounded-2xl border border-white/10 p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Truck className="w-5 h-5 text-brand-red" />
            <span className="font-heading text-white">DELIVERY DETAILS</span>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <div><span className="text-slate-500 text-xs">Pickup</span><p className="text-slate-300">{delivery.pickup_address}</p></div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-brand-red mt-0.5 flex-shrink-0" />
              <div><span className="text-slate-500 text-xs">Delivery</span><p className="text-slate-300">{delivery.delivery_address}</p></div>
            </div>
            {delivery.notes && (
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div><span className="text-slate-500 text-xs">Notes</span><p className="text-slate-300">{delivery.notes}</p></div>
              </div>
            )}
          </div>
        </div>

        {/* GPS Status Bar */}
        <div className={`rounded-2xl border p-4 mb-6 flex items-center justify-between ${
          gpsStatus === 'active' ? 'bg-green-500/10 border-green-500/20' :
          gpsStatus === 'error' ? 'bg-yellow-500/10 border-yellow-500/20' :
          'bg-brand-surface border-white/10'
        }`} data-testid="gps-status-bar">
          <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${gpsStatus === 'active' ? 'bg-green-400 animate-pulse' : gpsStatus === 'error' ? 'bg-yellow-400' : 'bg-slate-500'}`} />
            <div>
              <span className={`text-sm font-medium ${gpsStatus === 'active' ? 'text-green-400' : gpsStatus === 'error' ? 'text-yellow-400' : 'text-slate-400'}`}>
                {gpsStatus === 'active' ? 'GPS Active' : gpsStatus === 'error' ? 'GPS Unavailable' : 'GPS Starting...'}
              </span>
              {lastGpsTime && <span className="text-slate-500 text-xs ml-2">Updated {lastGpsTime.toLocaleTimeString()}</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={sendLocation}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-lg text-white text-xs hover:bg-white/15 transition-colors"
            data-testid="manual-gps-button"
          >
            <Navigation className="w-3.5 h-3.5" /> Update
          </button>
        </div>

        {/* POD Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Photo Capture */}
          <div className="bg-brand-surface rounded-2xl border border-white/10 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Camera className="w-5 h-5 text-brand-blue" />
              <span className="font-heading text-white text-sm">DELIVERY PHOTO</span>
            </div>
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="Delivery" className="w-full rounded-xl object-cover max-h-64" data-testid="photo-preview" />
                <button
                  type="button"
                  onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                  className="absolute top-2 right-2 p-2 bg-black/60 rounded-full text-white hover:bg-black/80"
                  data-testid="remove-photo"
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-12 border-2 border-dashed border-white/15 rounded-xl text-slate-400 hover:border-brand-red hover:text-brand-red transition-colors flex flex-col items-center gap-2"
                data-testid="take-photo-button"
              >
                <Camera className="w-8 h-8" />
                <span className="text-sm font-medium">Take Photo or Upload</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoChange}
              className="hidden"
              data-testid="photo-file-input"
            />
          </div>

          {/* Signature */}
          <div className="bg-brand-surface rounded-2xl border border-white/10 p-5">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-purple-400" />
              <span className="font-heading text-white text-sm">CUSTOMER SIGNATURE</span>
            </div>
            <SignaturePad onSignatureChange={setSignatureBlob} width={600} height={250} />
          </div>

          {/* Notes */}
          <div className="bg-brand-surface rounded-2xl border border-white/10 p-5">
            <label className="font-heading text-white text-sm mb-2 block">DELIVERY NOTES</label>
            <textarea
              value={podNotes}
              onChange={(e) => setPodNotes(e.target.value)}
              placeholder="Optional notes (e.g., left at door, handed to recipient)"
              rows={3}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:border-brand-red outline-none resize-none"
              data-testid="pod-notes-input"
            />
          </div>

          {submitError && (
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-xl" data-testid="pod-submit-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || (!photo && !signatureBlob)}
            className="w-full py-4 bg-green-600 hover:bg-green-700 text-white font-heading text-lg rounded-xl transition-all flex items-center justify-center gap-3 disabled:opacity-40"
            data-testid="pod-submit-button"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
            {submitting ? 'SUBMITTING...' : 'CONFIRM DELIVERY'}
          </button>
        </form>
      </main>
    </div>
  );
};

export default DriverDelivery;
